import { Divider, RecordContent, RecordFooter, useTitleChange } from '@msaf/core-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { HttpVerb } from '../../../../../types/api-types'
import { FormDelegate, PageNavigation } from '../../../../../types/routing-types'
import ConfirmTransition from '../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../components/errors/messages'
import WellDetailsAssignWell from '../../../../components/well-forms/details/well-details-assign-well'
import WellDetailsConsentEdit from '../../../../components/well-forms/details/well-details-consent-edit'
import WellDetailsDrillingEdit from '../../../../components/well-forms/details/well-details-drilling-edit'
import WellDetailsEditButtons from '../../../../components/well-forms/details/well-details-edit-buttons'
import WellDetailsIdentifiersEdit from '../../../../components/well-forms/details/well-details-identifiers-edit'
import WellDetailsLocationEdit from '../../../../components/well-forms/details/well-details-location-edit'
import NotesWrapperEdit from '../../../../components/well-forms/notes/notes-wrapper-edit'
import { useGetWellData, useLookup, useSaveWellData } from '../../../../hooks/use-api'
import useEditForm from '../../../../hooks/use-edit-form'
import useToastMessage from '../../../../hooks/use-toast-message'
import useUser from '../../../../hooks/use-user'
import { useWellId } from '../../../../hooks/use-well-id'

interface WellInitialSetupNavigationState {
  selectedCouncil: string
  isWellGrantedAsLegacy: boolean
  isExistingOrDecommissioned: boolean
  isWellNotDrilled: boolean
}

export function WellInitialSetup({ setIsDirty }: FormDelegate) {
  useTitleChange('New Well | Setup')
  const location = useLocation() as { state: WellInitialSetupNavigationState }
  const { user } = useUser()
  const id = useWellId()
  const navigate = useNavigate()

  const { isError: isLookupError, data, error: lookupError } = useLookup('listCouncils')
  const [newWellCouncilId, setNewWellCouncilId] = useState<string>()
  const [isExistingOrDecommissioned, setIsExistingOrDecommissioned] = useState<boolean>(
    location?.state?.isExistingOrDecommissioned ?? false,
  )
  const [isWellGrantedAsLegacy, setIsWellGrantedAsLegacy] = useState<boolean>(
    location?.state?.isWellGrantedAsLegacy ?? false,
  )
  const [selectedCompany, setSelectedCompany] = useState<object>({})

  const urls: Omit<PageNavigation, 'saveAndReturnRoute'> = {
    requestUrl: id === 'new' ? `wells/new` : `wells/${id}/new`,
    method: id === 'new' ? HttpVerb.POST : HttpVerb.PUT,
    saveAndContinueRoute: (id: string | number) => `/wells/${id}/edit/details`,
    saveRouteNewId: (id: string | number) => `/wells/${id}/edit/setup`,
    cancelButtonLabel: 'Cancel',
    cancelRoute: '/dashboard',
    navigateToSaveRouteState: { isWellGrantedAsLegacy, isExistingOrDecommissioned },
  }

  const stateQueryResult = useGetWellData(urls.requestUrl)
  const saveMutationResult = useSaveWellData(urls.requestUrl, urls.method)

  const {
    fields,
    queryError,
    saveError,
    editField,
    isLoading,
    isError: isWellDataError,
    isSaveError,
    setValue,
    handleSave,
    addField,
    removeField,
    validationErrors,
    isSaved,
  } = useEditForm({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Changes have been saved',
    error: saveError || queryError || lookupError,
    isSuccess: isSaved,
    isError: isSaveError || isWellDataError || isLookupError,
  })

  useEffect(() => {
    if (isWellDataError && queryError?.message === 'PERMISSION_DENIED') {
      navigate('/', { state: { isProgrammaticTransition: true } })
    }
  }, [isWellDataError, queryError, navigate])

  // this useEffect is used to set the council depending wether the
  // council is set via lookup field or set by the region modal
  useEffect(() => {
    // if council is set then we do not need to run this effect
    if (fields.council) {
      return
    }
    if (location?.state?.selectedCouncil) {
      const councilRegionId = data.find((item: any) => item.id === Number(location.state.selectedCouncil))?.id
      setNewWellCouncilId(councilRegionId)
      setValue('council', location.state.selectedCouncil)
    } else if (user?.councilId) {
      setValue('council', user.councilId)
    }
    // Excluded 'setValue' from dependency list to prevent
    // triggering multiple re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.selectedCouncil, data?.length, data, fields.council, user?.councilId])

  return (
    <>
      <RecordContent>
        <WellDetailsIdentifiersEdit
          setIsExistingOrDecommissioned={setIsExistingOrDecommissioned}
          isLoading={isLoading}
          fields={fields}
          validationErrors={validationErrors ?? []}
          isSetupRecord
          setValue={setValue}
          addField={addField}
          removeField={removeField}
          newWellCouncilId={newWellCouncilId}
        />
        <Divider />
        <WellDetailsConsentEdit
          isLoading={isLoading}
          fields={fields}
          validationErrors={validationErrors ?? []}
          isSetupRecord
          setValue={setValue}
          addField={addField}
          removeField={removeField}
          setIsWellGrantedAsLegacy={setIsWellGrantedAsLegacy}
        />
        <Divider />
        <WellDetailsLocationEdit
          isExistingOrDecommissioned={isExistingOrDecommissioned}
          isLoading={isLoading}
          fields={fields}
          validationErrors={validationErrors ?? []}
          setValue={setValue}
          isSetupRecord
        />
        <Divider />
        <WellDetailsDrillingEdit
          isLoading={isLoading}
          fields={fields}
          validationErrors={validationErrors ?? []}
          setValue={setValue}
          isSetupRecord
          isWellGrantedAsLegacy={isWellGrantedAsLegacy}
          isExistingOrDecommissioned={isExistingOrDecommissioned}
          handleCompanyValidation={(option) => setSelectedCompany(option)}
        />
        <Divider />
        <WellDetailsAssignWell
          isLoading={isLoading}
          fields={fields}
          validationErrors={validationErrors ?? []}
          setValue={setValue}
          isSetupRecord
          selectedCompany={selectedCompany}
          newWellCouncilId={newWellCouncilId}
        />
      </RecordContent>
      <RecordFooter
        notes={
          <NotesWrapperEdit
            notes={fields.notes ?? []}
            isLoading={isLoading}
            addField={(note) => addField('notes', note, true)}
            removeField={(index: number) => removeField('notes', index)}
            editField={(note, idx) => editField('notes', note, idx)}
            setIsDirty={setIsDirty}
          />
        }
      >
        <WellDetailsEditButtons urls={urls} handleSave={handleSave} />
      </RecordFooter>
    </>
  )
}

export default function WellSetupWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <WellInitialSetup setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
